"use client";

import { useEffect, useRef } from 'react';
import { ANON_ID_COOKIE_NAME } from '@/lib/constants';

interface ButtonTrackerProps {
  userId?: string | null;
}

export function ButtonTracker({ userId }: ButtonTrackerProps) {
  const lastTrackedUserActive = useRef<string | null>(null);
  useEffect(() => {
    // Ensure anonymous ID cookie exists and propagate to GA/GTM
    try {
      const getCookie = (name: string): string | null => {
        const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
        return match ? decodeURIComponent(match.pop() as string) : null
      }
      const setCookie = (name: string, value: string, days = 365) => {
        const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
      }
      const generateAnonId = (): string => {
        if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID()
        return 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
      }

      let anonId = getCookie(ANON_ID_COOKIE_NAME)
      if (!anonId) {
        anonId = generateAnonId()
        setCookie(ANON_ID_COOKIE_NAME, anonId)
      }

      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || []
      dataLayer.push({ event: 'identity_initialized', anonymous_id: anonId })

      // Set user_id via gtag set instead of config to avoid triggering additional pageviews
      if ((window as any).gtag && anonId) {
        ;(window as any).gtag('set', { user_id: anonId })
      }
    } catch {}

    // Track user_active on client when userId changes (no backend events hook)
    try {
      const anonMatch = document.cookie.match('(^|;)\\s*' + ANON_ID_COOKIE_NAME + '\\s*=\\s*([^;]+)');
      const clientId = anonMatch ? decodeURIComponent(anonMatch.pop() as string) : undefined;
      const cacheKey = `${userId || 'anon'}-${clientId || 'none'}`;
      if (cacheKey && lastTrackedUserActive.current !== cacheKey) {
        lastTrackedUserActive.current = cacheKey;
        fetch('/api/analytics/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: 'user_active', params: { source: 'client' }, userId, clientId })
        }).catch(() => {});
      }
    } catch {}

    const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const button = target.closest('button');
        const link = target.closest('a');
  
        if (typeof window !== 'undefined') {
          const dataLayer = (window as any).dataLayer = (window as any).dataLayer || []
          if (button) {
            const label = button.getAttribute('aria-label') || button.innerText || 'Unnamed Button';
            if ((window as any).gtag) {
              (window as any).gtag('event', 'click', {
                event_category: 'Button',
                event_label: label.trim(),
              });
            }
            dataLayer.push({ event: 'click', category: 'Button', label: label.trim() })
          } else if (link) {
            const label = link.getAttribute('aria-label') || link.innerText || (link as HTMLAnchorElement).href || 'Unnamed Link';
            if ((window as any).gtag) {
              (window as any).gtag('event', 'click', {
                event_category: 'Link',
                event_label: label.trim(),
              });
            }
            dataLayer.push({ event: 'click', category: 'Link', label: label.trim() })
          }
        }
      };

    // User ID is already set above via gtag('set'), no need to call config again

    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, [userId]);

  return null; // nothing visible
}